import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Lock, Mail, AlertCircle, ArrowLeft, User, Briefcase, Hash } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_jax-craft-remodel/artifacts/6r3h6sd1_Transparent%20Logo.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PortalLogin = () => {
  const [activeTab, setActiveTab] = useState('owner'); // 'owner' or 'contractor'
  
  // Owner login state
  const [email, setEmail] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');
  
  // Contractor login state
  const [contractorId, setContractorId] = useState('');
  const [contractorPassword, setContractorPassword] = useState('');
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/portal';

  const handleOwnerSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const user = await login(email, ownerPassword);
      toast.success(`Welcome back, ${user.name}!`);
      navigate(from, { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContractorSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/contractors/login`, {
        contractor_id: contractorId.toUpperCase(),
        password: contractorPassword
      });

      // Store token and contractor info
      localStorage.setItem('contractor_token', response.data.access_token);
      localStorage.setItem('contractor_info', JSON.stringify({
        ...response.data.contractor,
        agreement_signed: response.data.agreement_signed
      }));

      toast.success(`Welcome, ${response.data.contractor.name}!`);

      // Check if agreement is signed
      if (!response.data.agreement_signed) {
        navigate('/contractor/agreement');
      } else {
        navigate('/contractor');
      }
    } catch (err) {
      console.error('Contractor login error:', err);
      setError(err.response?.data?.detail || 'Invalid contractor ID or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4" data-testid="portal-login">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Back to Website */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 text-sm transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Website
        </Link>

        {/* Card */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 md:p-10">
          {/* Logo */}
          <div className="text-center mb-6">
            <img
              src={LOGO_URL}
              alt="Jax Remodeling Solutions"
              className="h-16 w-auto mx-auto mb-4"
            />
            <h1 className="font-heading text-2xl text-white">Access Portal</h1>
          </div>

          {/* Tabs */}
          <div className="flex mb-6 border border-white/10">
            <button
              onClick={() => { setActiveTab('owner'); setError(''); }}
              className={`flex-1 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'owner'
                  ? 'bg-[#C6A87C] text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
              data-testid="tab-owner"
            >
              <Briefcase className="w-4 h-4" />
              Owner
            </button>
            <button
              onClick={() => { setActiveTab('contractor'); setError(''); }}
              className={`flex-1 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'contractor'
                  ? 'bg-[#C6A87C] text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
              data-testid="tab-contractor"
            >
              <User className="w-4 h-4" />
              Contractor
            </button>
          </div>

          {/* Error */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 mb-6 flex items-center gap-3 text-sm"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </motion.div>
          )}

          {/* Owner Login Form */}
          {activeTab === 'owner' && (
            <motion.form
              key="owner"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              onSubmit={handleOwnerSubmit}
              className="space-y-6"
            >
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full bg-white/5 border border-white/10 text-white pl-12 pr-4 py-4 focus:border-[#C6A87C] focus:outline-none transition-colors"
                    placeholder="you@jaxremodelingsolutions.com"
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="password"
                    value={ownerPassword}
                    onChange={(e) => setOwnerPassword(e.target.value)}
                    required
                    className="w-full bg-white/5 border border-white/10 text-white pl-12 pr-4 py-4 focus:border-[#C6A87C] focus:outline-none transition-colors"
                    placeholder="••••••••"
                    data-testid="login-password"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#C6A87C] text-white py-4 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="login-submit"
              >
                {isLoading ? 'Signing in...' : 'Sign In as Owner'}
              </button>
            </motion.form>
          )}

          {/* Contractor Login Form */}
          {activeTab === 'contractor' && (
            <motion.form
              key="contractor"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              onSubmit={handleContractorSubmit}
              className="space-y-6"
            >
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">
                  Contractor ID
                </label>
                <div className="relative">
                  <Hash className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    value={contractorId}
                    onChange={(e) => setContractorId(e.target.value.toUpperCase())}
                    required
                    className="w-full bg-white/5 border border-white/10 text-white pl-12 pr-4 py-4 focus:border-[#C6A87C] focus:outline-none transition-colors font-mono uppercase"
                    placeholder="JAX-1234"
                    data-testid="contractor-id"
                  />
                </div>
                <p className="text-gray-500 text-xs mt-1">
                  Your contractor ID was provided by your supervisor
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="password"
                    value={contractorPassword}
                    onChange={(e) => setContractorPassword(e.target.value)}
                    required
                    className="w-full bg-white/5 border border-white/10 text-white pl-12 pr-4 py-4 focus:border-[#C6A87C] focus:outline-none transition-colors"
                    placeholder="••••••••"
                    data-testid="contractor-password"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#C6A87C] text-white py-4 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contractor-submit"
              >
                {isLoading ? 'Signing in...' : 'Sign In as Contractor'}
              </button>
            </motion.form>
          )}

          {/* Info Text */}
          <p className="text-gray-500 text-xs text-center mt-8">
            {activeTab === 'owner' ? (
              <>
                This portal is restricted to authorized personnel only.
                <br />
                All activity is monitored and logged.
              </>
            ) : (
              <>
                First time signing in? Your contractor ID (e.g., JAX-1234) and
                <br />
                temporary password were provided by your supervisor.
              </>
            )}
          </p>
        </div>
      </motion.div>
    </div>
  );
};
